/**
 * OverridesBadge — the default view's compact stand-in for the old inline
 * `(australia:50, hungary:50, …)` dump. One quiet pill states HOW MANY scopes
 * deviate; hovering it lays the values out as tiers (value → countries), with
 * campaign overrides listed under them, marked, because a campaign beats the
 * country it sits in. Scoped views never render this — they already resolve
 * to the one number that scope uses.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Globe2 } from 'lucide-react';

import { Tooltip } from '@/components/ui';

import type { ConditionOverrides } from '../format-overrides';
import { groupOverrides, overrideCount, type OverrideValueGroup } from './override-groups';

/**
 * Exported for the spec — the tooltip body without the hover plumbing.
 * Styled for the Tooltip's INVERTED surface (bg-foreground text-background):
 * theme tokens like text-muted-foreground go invisible there, so emphasis is
 * done with opacity and a translucent chip instead.
 */
export const OverridesTable: FC<{ groups: OverrideValueGroup[] }> = ({ groups }) => (
  <div className="flex max-w-xs flex-col gap-1.5 text-left">
    <p className="text-[10.5px] font-medium uppercase tracking-wide opacity-70">
      Threshold by country
    </p>
    {groups.map((g) => (
      <div key={g.value} className="flex items-baseline gap-2 text-[12px] leading-snug">
        <span className="shrink-0 rounded bg-background/20 px-1.5 font-mono font-semibold tabular-nums">
          {g.value}
        </span>
        <span className="min-w-0">
          {g.countries.join(', ')}
          {g.campaigns.length > 0 && (
            <span className="opacity-70" title="Campaign override — beats the country it sits in">
              {g.countries.length > 0 ? ' · ' : ''}▸ {g.campaigns.join(', ▸ ')}
            </span>
          )}
        </span>
      </div>
    ))}
    <p className="text-[10.5px] opacity-70">
      Everywhere else uses the base value. ▸ = one campaign only.
    </p>
  </div>
);

export const OverridesBadge: FC<{ condition: ConditionOverrides }> = ({ condition }) => {
  const n = overrideCount(condition);
  if (n === 0) return null;
  return (
    <Tooltip interactive side="bottom" content={<OverridesTable groups={groupOverrides(condition)} />}>
      <span className="inline-flex cursor-help items-center gap-1 whitespace-nowrap rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10.5px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
        <Globe2 className="h-3 w-3 shrink-0" aria-hidden />
        {n} override{n === 1 ? '' : 's'}
      </span>
    </Tooltip>
  );
};

export default OverridesBadge;
